import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import { AlertService } from '../../../../services/alert.service';
import Icon from '../../../../components/forms/icon.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import LogData from '../../../../components/logs/log-data.component';
import { FlowsService } from '../../../../services/flows.service';

interface EditFlowProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function EditFlowModal({ show, onRequestClose, payload }: EditFlowProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const dynamicTitle = `${t('general.edit')} ${payload?.name}`;
  const dynamicSubtitle = `${t('account-page.modal-edit-account.subtitle-edit-account')} ${payload?.name}`;
  useModal(modalRef, show);
  const defaultValues = {
    _id: '',
    name: ''
  };
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });
  const onSubmit = async (data: any) => {
    const requestBody = {
      _id: data._id,
      name: data.name
    };
    const buttonId = `editAccount-${data._id}`;
    await AlertService.warning(
      `<h2>${t('account-page.modal-edit-account.edit-account-title')}</h2>`,
      `<div class="text-gray-700 fs-6">
        <p class="mb-2">${t('account-page.modal-edit-account.edit-account-text')}</p>
        <ul class="list-unstyled">
          <li class="my-2 text-center"><strong>${t('account-page.modal-edit-account.name-label')}:</strong> ${data.name}</li>
        </ul>
      </div>`,
      async () => {
        try {
          await FlowsService.editFlow(requestBody);
          LogData(requestBody, buttonId, window.location.href);
          AlertService.toastSuccess(t('account-page.alert-success-edit'));
          onRequestClose(true);
          reset(defaultValues);
        } catch (errorCode: any) {
          console.log('Error: ', errorCode);
          AlertService.toastError(errorCode.error || errorCode);
        }
      },
      t('general.btn-add-modal')
    );
  };
  useEffect(() => {
    reset({
      _id: payload?._id ? payload?._id : defaultValues?._id,
      name: payload?.name ? payload?.name : defaultValues?.name
    });
  }, [payload]);
  return (
    <>
      <div aria-hidden="true" className="modal fade" ref={modalRef} data-bs-backdrop="static">
        <div className="modal-dialog modal-mw-900px modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header justify-content-end border-0 pb-0">
              <div
                className="btn btn-sm btn-icon btn-active-light-primary"
                onClick={() => onRequestClose(false)}
              >
                <Icon icon="ki-cross" size="fs-1" />
              </div>
            </div>
            <div className="modal-body pt-0 pb-0 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h2>
                  {t('account-page.modal-edit-account.title-edit-account')}
                  <br />
                  <span className="text-primary">
                    {' '}
                    {payload?.company?.name} - {payload?.name}
                  </span>
                </h2>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="fv-row mb-5">
                  <Title title={dynamicTitle} subtitle={dynamicSubtitle} icon="ki-notepad-edit" />
                  <div className="row row-cols-1 row-cols-sm-2 rol-cols-md-1 row-cols-lg-1">
                    <div className="col">
                      <div className="fv-row mb-7">
                        <Input
                          label={t('account-page.modal-edit-account.label-name')}
                          requiredIndicator="required"
                          placeholder={t('account-page.modal-edit-account.name-placeholder')}
                          errors={errors.name}
                          {...register('name', {
                            required: true,
                            setValueAs: (value) => value.trim()
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light hover-scale"
                    onClick={() => onRequestClose(false)}
                  >
                    {t('general.close')}
                  </button>
                  <Button
                    type="submit"
                    id="sendData"
                    className="btn btn-primary ms-3 hover-scale"
                    disabled={!isValid}
                    isLoading={isSubmitting}
                  >
                    {t('general.edit')}
                    <Icon icon="ki-send" size="fs-1" type="" />
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditFlowModal;
